/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  APP.TSX - NOTOMORROW3D MAIN APPLICATION                                  ║
 * ║  "Humanity's Last Stand Against THALIA" - 3D Holographic Resistance HQ    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * Layout:
 * - 3D Nixie tube Singularity countdown as dramatic centerpiece
 * - Heavy glassmorphism on all panels
 * - Neon cyan/magenta/purple color scheme
 * - Pure black background with THALIA presence
 */

import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════════════════
// CRITICAL PATH - Always loaded immediately
// Timer, Pot, Buy Keys, Dividends = 80% of user experience
// ═══════════════════════════════════════════════════════════════════════════
import HeroStats from './components/HeroStats';
import BuyKeys from './components/BuyKeys';
import Dividends from './components/Dividends';
import StickyTimer from './components/StickyTimer';
import DangerZone, { useDangerZoneClass } from './components/DangerZone';
import WinnerCelebration from './components/WinnerCelebration';
import { useFactionTheme } from './hooks/useFactionTheme';
import type { ChaosEvent } from './components/ChaosChiron';
import type { GameAtmosphere } from './components/MatrixRain';

// ═══════════════════════════════════════════════════════════════════════════
// LAZY LOADED - Non-critical, loaded on demand
// Reduces initial bundle size for faster mobile load
// ═══════════════════════════════════════════════════════════════════════════
const LandingPage = lazy(() => import('./components/LandingPage'));
const WalletConnect = lazy(() => import('./components/WalletConnect'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const Referral = lazy(() => import('./components/Referral'));
const RoundHistory = lazy(() => import('./components/RoundHistory'));
const Sharing = lazy(() => import('./components/Sharing'));
const HowToPlay = lazy(() => import('./components/HowToPlay'));
const LiveActivityFeed = lazy(() => import('./components/LiveActivityFeed'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const TeamJackpotClaim = lazy(() => import('./components/TeamJackpotClaim'));
const TickerTape = lazy(() => import('./components/TickerTape'));
const DegenTitles = lazy(() => import('./components/DegenTitles'));
const MatrixRain = lazy(() => import('./components/MatrixRain'));
const PushNotifications = lazy(() => import('./components/PushNotifications'));

// Named export lazy wrappers
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const PriceChart = lazy(() => import('./components/PriceChart').then(m => ({ default: m.PriceChart })));
const OnboardingTutorial = lazy(() => import('./components/OnboardingTutorial').then(m => ({ default: m.OnboardingTutorial })));
const THALIACommentary = lazy(() => import('./components/THALIACommentary').then(m => ({ default: m.THALIACommentary })));
const ChaosChiron = lazy(() => import('./components/ChaosChiron').then(m => ({ default: m.ChaosChiron })));
const StreakDisplay = lazy(() => import('./components/StreakDisplay').then(m => ({ default: m.StreakDisplay })));
const NetworkStatusBar = lazy(() => import('./components/NetworkStatusBar').then(m => ({ default: m.NetworkStatusBar })));
const TermsOfService = lazy(() => import('./components/TermsOfService').then(m => ({ default: m.TermsOfService })));

// UX Improvement Components - Information hierarchy & contextual education
const ActiveConditions = lazy(() => import('./components/ActiveConditions').then(m => ({ default: m.ActiveConditions })));
const GameLog = lazy(() => import('./components/GameLog').then(m => ({ default: m.GameLog })));
const ResistancePoints = lazy(() => import('./components/ResistancePoints').then(m => ({ default: m.ResistancePoints })));
const THALIAHint = lazy(() => import('./components/THALIAHint').then(m => ({ default: m.THALIAHint })));

// Error handling
import { ErrorBoundary, SafeSection } from './components/ErrorBoundary';

// Always loaded - core hooks & providers
import { NotificationProvider } from './components/NotificationSystem';
import { TutorialButton } from './components/OnboardingTutorial';
import { SoundProvider, useSoundEffects } from './hooks/useSoundEffects';
import { useVisualEffects } from './hooks/useVisualEffects';
import { useGameEvents, type GameEvent } from './hooks/useGameEvents';
import { useEventExplainer, EventExplainerOverlay } from './components/EventExplainer';
import { HelpButton } from './components/Glossary';
import { DemoModeProvider, useDemoMode, DemoBanner, DemoStartModal } from './components/DemoMode';
import { DemoSiteBanner } from './components/DemoSiteBanner';
import { BetaWarningBanner } from './components/BetaWarningBanner';
import { PlayerNameModal } from './components/PlayerNameModal';
import { NarrativeToast } from './components/NarrativeToast';
import type { EventType } from './services/narrative';
import { InstallPrompt } from './components/InstallPrompt';
import { TeamChat } from './components/TeamChat';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { OfflineBanner } from './components/OfflineBanner';

// Utils
import { getGameContract } from './utils/contract';
import { fetchPAWPrice } from './utils/price';
import { toast } from 'react-hot-toast';
import { ethers } from 'ethers';

// Helper to format PAW amounts for narrative display
function formatPAWForNarrative(pawWei: string): string {
  try {
    const amount = parseFloat(ethers.formatEther(pawWei));
    if (amount >= 1_000_000_000_000_000) return `${(amount / 1_000_000_000_000_000).toFixed(1)}Q PAW`;
    if (amount >= 1_000_000_000_000) return `${(amount / 1_000_000_000_000).toFixed(1)}T PAW`;
    if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(1)}B PAW`;
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M PAW`;
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K PAW`;
    return `${amount.toFixed(0)} PAW`;
  } catch {
    return 'some PAW';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// GLASS HEADER COMPONENT - 3D Glassmorphism Navigation
// ═══════════════════════════════════════════════════════════════════════════

function GlassHeader({ 
  currentPage, 
  setCurrentPage, 
  handleWalletConnect, 
  account, 
  timeRemaining,
  onEditName 
}: {
  currentPage: 'game' | 'howto' | 'analytics' | 'terms';
  setCurrentPage: (page: 'game' | 'howto' | 'analytics' | 'terms') => void;
  handleWalletConnect: (address: string | null) => void;
  account: string | null;
  timeRemaining: number;
  onEditName?: () => void;
}) {
  const navItems = [
    { id: 'game' as const, label: 'Game', icon: '◈', color: 'cyan' },
    { id: 'analytics' as const, label: 'Analytics', icon: '◇', color: 'magenta' },
    { id: 'howto' as const, label: 'Help', icon: '◆', color: 'purple' },
  ];

  return (
    <header className="relative mb-4 md:mb-8">
      <div className="glass-panel-3d p-3 md:p-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-3 md:gap-4">
          {/* Logo Section */}
          <motion.div
            className="flex items-center gap-4"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Logo with glow */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-purple rounded-xl blur-md opacity-50 group-hover:opacity-80 transition-opacity" />
              <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-xl bg-void-black border border-neon-cyan/30 flex items-center justify-center overflow-hidden">
                <img 
                  src="/logo.jpg" 
                  alt="NT3D" 
                  className="w-10 h-10 md:w-12 md:h-12 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <h1 className="text-xl md:text-2xl font-orbitron font-bold text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-purple">
                NO TOMORROW 3D
              </h1>
              <div className="flex items-center gap-2">
                <motion.div
                  className="w-2 h-2 rounded-full bg-neon-cyan shadow-[0_0_8px_#00ffff]"
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
                <span className="text-xs text-gray-500 font-mono tracking-widest">FOMO MACHINE</span>
              </div>
            </div>
          </motion.div>

          {/* Navigation */}
          <motion.nav
            className="flex items-center gap-1 p-1 rounded-xl bg-void-black/50 border border-gray-800"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`relative px-4 py-2 rounded-lg font-orbitron text-sm transition-all duration-300 ${
                  currentPage === item.id
                    ? 'border'
                    : 'text-gray-500 hover:text-white border border-transparent'
                }`}
                style={{
                  color: currentPage === item.id 
                    ? item.color === 'cyan' ? '#00ffff' : item.color === 'magenta' ? '#ff0080' : '#9932cc'
                    : undefined,
                  backgroundColor: currentPage === item.id 
                    ? item.color === 'cyan' ? 'rgba(0,255,255,0.1)' : item.color === 'magenta' ? 'rgba(255,0,128,0.1)' : 'rgba(153,50,204,0.1)'
                    : undefined,
                  borderColor: currentPage === item.id 
                    ? item.color === 'cyan' ? 'rgba(0,255,255,0.3)' : item.color === 'magenta' ? 'rgba(255,0,128,0.3)' : 'rgba(153,50,204,0.3)'
                    : undefined,
                }}
              >
                <span className="mr-1">{item.icon}</span>
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            ))}
          </motion.nav>

          {/* Right Controls */}
          <motion.div
            className="flex items-center gap-3"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="hidden md:block">
              <TutorialButton />
            </div>
            <div className="hidden md:block">
              <HelpButton inline />
            </div>
            <PushNotifications timeRemaining={timeRemaining} compact={true} />
            <WalletConnect 
              onConnect={handleWalletConnect} 
              account={account} 
              onEditName={onEditName}
            />
          </motion.div>
        </div>

        {/* Bottom accent line */}
        <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-neon-cyan/30 to-transparent" />
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function App() {
  return (
    <ErrorBoundary name="App">
      <SoundProvider>
        <DemoModeProvider>
          <AppContent />
        </DemoModeProvider>
      </SoundProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  const [account, setAccount] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(-1); // -1 = loading, 0 = round ended
  const [roundDeadline, setRoundDeadline] = useState<number>(0); // Unix timestamp of round deadline
  const [pot, setPot] = useState<bigint>(0n);
  const [jackpotPool, setJackpotPool] = useState<bigint>(0n);
  const [currentPrice, setCurrentPrice] = useState<bigint>(0n);
  const [lastBuyer, setLastBuyer] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [referrerFromUrl, setReferrerFromUrl] = useState<string | null>(null);
  const [showLanding, setShowLanding] = useState<boolean>(() => {
    // Dev bypass: ?dev in URL skips landing page for testing
    if (window.location.search.includes('dev')) return false;
    // Check if user has already accepted disclaimer
    const accepted = localStorage.getItem('nt3d_disclaimer_accepted') === 'true';
    console.log('🚀 App initializing, disclaimer accepted:', accepted);
    return !accepted;
  });
  const [currentPage, setCurrentPage] = useState<'game' | 'howto' | 'analytics' | 'terms'>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('page') === 'terms') return 'terms';
    return 'game';
  });
  const [isEndingRound, setIsEndingRound] = useState(false);
  const [currentRoundId, setCurrentRoundId] = useState<number>(1);
  const [showPlayerNameModal, setShowPlayerNameModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [totalKeysSold, setTotalKeysSold] = useState<number>(0);
  const [recentPurchaseCount, setRecentPurchaseCount] = useState<number>(0);
  const [chaosRecentlyTriggered, setChaosRecentlyTriggered] = useState(false);
  
  // New feature states
  // showTeamChat removed – chat is now always inline below the leaderboard
  const [playerTeam, setPlayerTeam] = useState<number>(0);
  
  // Sticky timer & winner celebration states
  const [showStickyTimer, setShowStickyTimer] = useState(false);
  const [showWinnerCelebration, setShowWinnerCelebration] = useState(false);
  const [roundWinner, setRoundWinner] = useState<string>('');
  const [winnerPot, setWinnerPot] = useState<bigint>(0n);
  const [winnerRoundId, setWinnerRoundId] = useState<number>(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const buyKeysRef = useRef<HTMLDivElement>(null);
  
  // Chaos visual state
  const [extinctionActive, setExtinctionActive] = useState(false);
  const [flashCrashActive, setFlashCrashActive] = useState(false);
  const [fomoSurgeActive, setFomoSurgeActive] = useState(false);
  const [assassinActive, setAssassinActive] = useState(false);
  const [carnivalActive, setCarnivalActive] = useState(false);
  const [activeChaosEvents, setActiveChaosEvents] = useState<ChaosEvent[]>([]);
  
  // Narrative toast state for AI-generated event stories
  const [narrativeEvent, setNarrativeEvent] = useState<{
    type: EventType;
    team?: number;
    player?: string;
    amount?: string;
    details?: string;
  } | undefined>(undefined);
  
  // Secret sauce hooks
  useSoundEffects(); // Initialize sound system
  useVisualEffects(); // Initialize visual effects
  const demoMode = useDemoMode();
  const eventExplainer = useEventExplainer();
  
  // Faction personalization & danger zone atmosphere
  useFactionTheme(playerTeam);
  useDangerZoneClass(timeRemaining, currentRoundId % 10 === 0);
  
  // Intersection Observer for sticky timer
  // Re-run when currentPage/isLoading/showLanding change since heroRef only exists on the game page
  useEffect(() => {
    const el = heroRef.current;
    if (!el) { setShowStickyTimer(false); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyTimer(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentPage, isLoading, showLanding]);
  
  // WebSocket for real-time game events
  const handleGameEvent = useCallback((event: GameEvent) => {
    console.log('[NT3D] WebSocket event:', event.type, event.data);
    
    // Track recent purchase activity for THALIA context
    if (event.type === 'keys_purchased') {
      setRecentPurchaseCount(prev => prev + 1);
    }
    
    // Track chaos events for THALIA context + visual shifts
    if (['ghost_key', 'extinction_event', 'blood_moon', 'surge_protocol', 'assassin_mode'].includes(event.type)) {
      setChaosRecentlyTriggered(true);
      setTimeout(() => setChaosRecentlyTriggered(false), 30000); // Clear after 30s
    }
    
    // Chaos visual shift triggers
    if (event.type === 'extinction_event') {
      setExtinctionActive(true);
      setTimeout(() => setExtinctionActive(false), 5000);
    }
    if (event.type === 'flash_crash') {
      setFlashCrashActive(true);
      // Flash crash has a duration, clear after event data says so or 60s default
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setFlashCrashActive(false), duration);
    }
    if (event.type === 'surge_protocol' || event.type === 'fomo_surge') {
      setFomoSurgeActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setFomoSurgeActive(false), duration);
    }
    if (event.type === 'assassin_mode') {
      setAssassinActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 120000;
      setTimeout(() => setAssassinActive(false), duration);
    }
    if (event.type === 'chaos_carnival') {
      setCarnivalActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setCarnivalActive(false), duration);
    }
    
    // Feed chaos events to ChaosChiron ticker
    const chaosTypes = [
      'flash_crash', 'fomo_surge', 'surge_protocol', 'extinction_event',
      'ghost_key', 'blood_moon', 'assassin_mode', 'chaos_carnival',
      'volcanic_jackpot', 'charity_chaos', 'groundhog_day', 'whale_alert',
    ];
    if (chaosTypes.includes(event.type)) {
      const chaosEvent: ChaosEvent = {
        id: `${event.type}-${Date.now()}`,
        type: event.type as ChaosEvent['type'],
        message: event.data.message || `${event.type.replace(/_/g, ' ').toUpperCase()} triggered!`,
        timestamp: Date.now(),
        duration: event.data.duration ? parseInt(event.data.duration) * 1000 : 30000,
        severity: ['extinction_event', 'flash_crash', 'blood_moon', 'assassin_mode'].includes(event.type) 
          ? 'critical' 
          : ['fomo_surge', 'surge_protocol', 'chaos_carnival'].includes(event.type) 
            ? 'warning' 
            : 'info',
      };
      setActiveChaosEvents(prev => [...prev.slice(-9), chaosEvent]); // Keep last 10
      // Auto-expire after duration
      setTimeout(() => {
        setActiveChaosEvents(prev => prev.filter(e => e.id !== chaosEvent.id));
      }, chaosEvent.duration || 30000);
    }
    
    // Winner celebration trigger
    if (event.type === 'round_ended') {
      setRoundWinner(event.data.winner || '');
      setWinnerPot(BigInt(event.data.potSize || '0'));
      setWinnerRoundId(parseInt(event.data.roundId || '0'));
      setShowWinnerCelebration(true);
    }
    
    // Map WebSocket events to narrative event types (THALIA narrative)
    const eventTypeMap: Record<string, EventType> = {
      'keys_purchased': 'key_purchase',
      'round_started': 'round_start', 
      'round_ended': 'round_end',
      'assassin_mode': 'assassin_mode',       // TERMINAL
      'blood_moon': 'blood_moon_start',       // SURGE
      'surge_protocol': 'blood_moon_start',   // THALIA naming
      'ghost_key': 'ghost_key',               // PHANTOM
      'extinction_event': 'timer_critical',   // RECALIBRATION
    };
    
    const narrativeType = eventTypeMap[event.type];
    if (!narrativeType) return;
    
    // Only show narratives for significant events (not every single key purchase)
    const isSignificant = 
      event.type !== 'keys_purchased' || 
      (event.data.keysBought && parseInt(event.data.keysBought) >= 10) ||
      event.data.isGhostKey;
    
    if (isSignificant) {
      setNarrativeEvent({
        type: narrativeType,
        team: event.data.teamId,
        player: event.data.buyer || event.data.winner,
        amount: event.data.pawAmount ? formatPAWForNarrative(event.data.pawAmount) : undefined,
        details: event.data.message,
      });
    }
  }, []);
  
  const { connected: wsConnected } = useGameEvents({
    autoConnect: true,
    onEvent: handleGameEvent,
  });
  
  // Decay recent purchase counter every 60s to simulate a rolling window
  useEffect(() => {
    const decay = setInterval(() => {
      setRecentPurchaseCount(prev => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(decay);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // DEMO MODE: Computed display values (switches between real and demo data)
  // ─────────────────────────────────────────────────────────────────────────
  const displayTimeRemaining = demoMode.isDemo ? demoMode.demo.timeRemaining : timeRemaining;
  const displayPot = demoMode.isDemo ? demoMode.demo.pot : pot;
  const displayJackpotPool = demoMode.isDemo ? demoMode.demo.pot / 10n : jackpotPool; // Demo jackpot is 10% of pot
  const displayCurrentPrice = demoMode.isDemo ? demoMode.demo.keyPrice : currentPrice;
  const displayLastBuyer = demoMode.isDemo ? (demoMode.demo.lastBuyer || 'Demo Player') : lastBuyer;
  // Note: displayRoundId available via demoMode.isDemo ? demoMode.demo.round : currentRoundId
  
  // ─────────────────────────────────────────────────────────────────────────
  // PARSE URL REFERRER
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref && ref.startsWith('0x')) {
      setReferrerFromUrl(ref);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH PLAYER TEAM (for team chat)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPlayerTeam = async () => {
      if (!account) {
        setPlayerTeam(0);
        return;
      }
      try {
        const contract = await getGameContract();
        if (!contract) return;
        const info = await contract.getPlayerInfo(account);
        // getPlayerInfo returns (keysOwned, teamId, lastPurchaseTime, ...)
        setPlayerTeam(Number(info.teamId || 0));
      } catch (error) {
        console.error('Failed to fetch player team:', error);
      }
    };
    fetchPlayerTeam();
  }, [account, refreshTrigger]);

  // FETCH PAW PRICE (for display only, not for pricing)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPAWPriceForDisplay = async () => {
      try {
        await fetchPAWPrice();
        // PAW price is now handled by individual components
      } catch (error) {
        console.error('Failed to fetch PAW price:', error);
      }
    };
    
    fetchPAWPriceForDisplay();
    
    // Update price every 10 seconds for more accurate pricing
    const interval = setInterval(fetchPAWPriceForDisplay, 10000);
    return () => clearInterval(interval);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // FETCH GAME DATA
  // ─────────────────────────────────────────────────────────────────────────
  const fetchGameData = useCallback(async () => {
    try {
      console.log('[NT3D] Fetching game data...');
      const gameContract = await getGameContract();
      if (!gameContract) {
        console.warn('[NT3D] Game contract not available');
        setIsLoading(false);
        return;
      }
      
      console.log('[NT3D] Contract ready, calling getRoundInfo...');
      // Fetch current round data (contract uses getRoundInfo not getCurrentRoundInfo)
      // Note: We use getFullKeyPrice() instead of roundData.currentPrice because
      // getCurrentKeyPrice() in the contract uses all-time totalKeys instead of
      // round-specific totalKeysSold, causing price to not reset between rounds.
      const [roundData, jackpot, correctPrice] = await Promise.all([
        gameContract.getRoundInfo(),
        gameContract.jackpotPool(),
        gameContract.getFullKeyPrice(), // This uses round-specific key count
      ]);
      
      console.log('[NT3D] Round data received:', {
        roundId: Number(roundData.roundId),
        pot: roundData.pot?.toString(),
        timeRemaining: Number(roundData.timeRemaining)
      });
      
      const deadline = Number(roundData.deadline || 0);
      const newTimeRemaining = Number(roundData.timeRemaining || 0);
      setRoundDeadline(deadline);
      setTimeRemaining(newTimeRemaining);
      setPot(roundData.pot || 0n);
      setJackpotPool(jackpot || 0n);
      setCurrentPrice(correctPrice || roundData.currentPrice || 0n); // Use getFullKeyPrice() for correct round-based price
      setLastBuyer(roundData.lastBuyer || '');
      setCurrentRoundId(Number(roundData.roundId || 1));
      setTotalKeysSold(Number(roundData.totalKeysSold || 0));
      
      // Reset ending round state if we have time remaining (fixes desync issues)
      if (newTimeRemaining > 0) {
        setIsEndingRound(false);
      }
      
    } catch (error) {
      console.error('[NT3D] Failed to fetch game data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);
  
  // Poll for updates
  useEffect(() => {
    const interval = setInterval(fetchGameData, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [fetchGameData]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // COUNTDOWN TIMER
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (roundDeadline > 0) {
        // Calculate time from deadline for accuracy — picks up extensions automatically
        const now = Math.floor(Date.now() / 1000);
        const remaining = roundDeadline - now;
        setTimeRemaining(remaining > 0 ? remaining : 0);
      } else {
        setTimeRemaining((prev) => {
          // Don't decrement during loading (-1) or if already at 0
          if (prev <= 0) return prev;
          return prev - 1;
        });
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [roundDeadline]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // AUTO-REFRESH WHEN ROUND ENDS (keeper handles actual round ending)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Only trigger when timer reaches 0 (not -1 loading state) and not already ending
    if (timeRemaining !== 0 || isEndingRound || isLoading) return;
    
    // Show notification that round is ending
    setIsEndingRound(true);
    toast('⏰ Round complete! Finalizing prizes...', {
      duration: 10000,
      icon: '🏆',
      style: { background: '#1a1a2e', color: '#eab308', border: '1px solid #eab308' }
    });
    
    // Poll more frequently to detect new round
    const pollForNewRound = setInterval(async () => {
      try {
        const gameContract = await getGameContract();
        if (!gameContract) return;
        
        const roundData = await gameContract.getRoundInfo();
        const newTimeRemaining = Number(roundData.timeRemaining || 0);
        
        // If timer is > 0, new round has started
        if (newTimeRemaining > 0) {
          console.log('✅ New round detected! Time remaining:', newTimeRemaining);
          toast.success('🎮 New round started! Game on!', { duration: 5000 });
          setIsEndingRound(false);
          fetchGameData();
          clearInterval(pollForNewRound);
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 3000); // Check every 3 seconds
    
    // Clean up after 2 minutes max
    const timeout = setTimeout(() => {
      clearInterval(pollForNewRound);
      setIsEndingRound(false);
      fetchGameData();
    }, 120000);
    
    return () => {
      clearInterval(pollForNewRound);
      clearTimeout(timeout);
    };
  }, [timeRemaining, isEndingRound, fetchGameData]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleWalletConnect = (address: string | null) => {
    setAccount(address);
    fetchGameData();
  };
  
  const handlePurchase = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
    // Immediate fetch to get updated timer
    fetchGameData();
    // Delayed re-fetch to catch timer extension after block confirmation
    setTimeout(() => fetchGameData(), 3000);
    setTimeout(() => fetchGameData(), 6000);
  }, [fetchGameData]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // ADMIN ACCESS - Redirect to separate admin site
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        // Redirect to admin site (protected by Cloudflare Access)
        window.open('https://admin.nt3d.fun', '_blank');
        console.log('🔓 Redirecting to admin site');
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // SHOW LANDING PAGE IF USER HASN'T ACCEPTED DISCLAIMER
  // ─────────────────────────────────────────────────────────────────────────
  if (showLanding) {
    return <Suspense fallback={<div className="min-h-screen bg-black" />}><LandingPage onEnter={() => setShowLanding(false)} /></Suspense>;
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ─────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          className="relative z-10 text-center glass-panel-3d p-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Animated loading indicator */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-neon-cyan/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-2 rounded-full border-2 border-neon-magenta/30"
              animate={{ rotate: -360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-4 rounded-full border-2 border-neon-purple/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-3xl">◈</span>
            </motion.div>
          </div>
          
          <h1 className="font-orbitron text-2xl text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-purple mb-3">
            INITIALIZING NEURAL LINK
          </h1>
          <p className="text-gray-500 text-sm font-mono">Connecting to blockchain...</p>
        </motion.div>
      </div>
    );
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // MATRIX ATMOSPHERE — computed from game state priority
  // ─────────────────────────────────────────────────────────────────────────
  const matrixAtmosphere: GameAtmosphere = (() => {
    if (extinctionActive) return 'extinction';
    if (currentRoundId % 10 === 0) return 'blood_moon';
    if (carnivalActive) return 'carnival';
    if (assassinActive) return 'assassin';
    if (fomoSurgeActive) return 'fomo_surge';
    if (flashCrashActive) return 'flash_crash';
    if (chaosRecentlyTriggered) return 'chaos';
    if (displayTimeRemaining >= 0 && displayTimeRemaining < 60) return 'critical';
    if (displayTimeRemaining >= 0 && displayTimeRemaining < 300) return 'danger';
    if (displayTimeRemaining >= 0 && displayTimeRemaining < 1800) return 'warning';
    return 'normal';
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <NotificationProvider>
    <div className="min-h-screen text-white overflow-x-hidden bg-black relative">
      {/* Offline Detection Banner — shows when internet drops */}
      <OfflineBanner />

      {/* Matrix Rain Background - THALIA's presence, reactive to game state */}
      {typeof window !== 'undefined' && (
        <Suspense fallback={null}>
          <MatrixRain characters="thalia" atmosphere={matrixAtmosphere} />
        </Suspense>
      )}
      
      {/* Cyber Grid Overlay */}
      <div className="fixed inset-0 cyber-grid pointer-events-none z-0" />
      
      {/* Subtle Scanlines */}
      <div className="fixed inset-0 pointer-events-none z-40" style={{
        background: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.03) 0px, rgba(0,0,0,0.03) 1px, transparent 1px, transparent 3px)'
      }} />
      
      {/* ═══ Danger Zone - Atmospheric visual shifts ═══ */}
      <DangerZone
        timeRemaining={displayTimeRemaining}
        isBloodMoon={currentRoundId % 10 === 0}
        chaosActive={chaosRecentlyTriggered}
        extinctionActive={extinctionActive}
        flashCrashActive={flashCrashActive}
        fomoSurgeActive={fomoSurgeActive}
        assassinActive={assassinActive}
        carnivalActive={carnivalActive}
      />
      
      {/* ═══ Sticky Timer - Appears when hero section scrolls out of view ═══ */}
      <StickyTimer
        timeRemaining={displayTimeRemaining}
        pot={displayPot}
        currentPrice={displayCurrentPrice}
        lastBuyer={displayLastBuyer}
        visible={showStickyTimer && currentPage === 'game'}
        onBuyClick={() => buyKeysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
      />
      
      {/* ═══ Winner Celebration - Full-screen when round ends ═══ */}
      {showWinnerCelebration && (
        <WinnerCelebration
          winner={roundWinner}
          potSize={winnerPot}
          roundId={winnerRoundId}
          isBloodMoon={winnerRoundId % 10 === 0}
          onDismiss={() => setShowWinnerCelebration(false)}
        />
      )}
      
      {/* Demo SITE Banner (for demo.nt3d.fun) */}
      <DemoSiteBanner walletAddress={account || undefined} />
      
      {/* Beta Warning Banner (for mainnet beta testing) */}
      <BetaWarningBanner />
      
      {/* Demo Mode Banner (for simulation mode) */}
      <DemoBanner />
      
      {/* Demo Start Modal */}
      <DemoStartModal 
        isOpen={showDemoModal} 
        onClose={() => setShowDemoModal(false)}
        onStart={() => {
          setShowDemoModal(false);
          // Demo mode is already started by DemoStartModal
        }}
      />
      
      {/* Player Name Modal */}
      {account && (
        <PlayerNameModal
          isOpen={showPlayerNameModal}
          onClose={() => setShowPlayerNameModal(false)}
          address={account}
        />
      )}
      
      {/* Event Explainer - Shows "What Just Happened?" overlays */}
      <EventExplainerOverlay 
        explanation={eventExplainer.activeExplanation} 
        onDismiss={eventExplainer.dismiss} 
      />
      
      {/* Onboarding Tutorial */}
      <Suspense fallback={null}>
        <OnboardingTutorial onComplete={() => {}} />
      </Suspense>
      
      {/* Toast Notifications - Styled for new design */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(0, 0, 0, 0.9)',
            color: '#ffffff',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.1)',
          },
        }}
      />
      
      {/* Main Container */}
      <div className="relative z-10 container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-7xl ios-bottom-padding">
       <Suspense fallback={<div className="flex items-center justify-center min-h-[200px]"><div className="w-8 h-8 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" /></div>}>
        {/* ═══════════════════════════════════════════════════════════════════
            HEADER - 3D GLASSMORPHISM
            ═══════════════════════════════════════════════════════════════════ */}
        <GlassHeader
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          handleWalletConnect={handleWalletConnect}
          account={account}
          timeRemaining={timeRemaining}
          onEditName={() => setShowPlayerNameModal(true)}
        />

        {/* ═══════════════════════════════════════════════════════════════════
            PAGE CONTENT - Conditional rendering based on currentPage
            ═══════════════════════════════════════════════════════════════════ */}
        {currentPage === 'howto' ? (
          <HowToPlay />
        ) : currentPage === 'analytics' ? (
          /* Analytics Page */
          <div className="space-y-6">
            {/* Analytics Dashboard */}
            <AnalyticsDashboard account={account} />
            
            {/* Price Chart & Transaction History */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PriceChart />
              <TransactionHistory account={account} />
            </div>
          </div>
        ) : currentPage === 'terms' ? (
          /* Terms of Service Page */
          <SafeSection name="TermsOfService">
          <Suspense fallback={<div className="text-center py-12 text-gray-500">Loading...</div>}>
            <TermsOfService onBack={() => setCurrentPage('game')} />
          </Suspense>
          </SafeSection>
        ) : (
          <>
        {/* ═══════════════════════════════════════════════════════════════════
            THALIA COMMENTARY - AI observer presence
            ═══════════════════════════════════════════════════════════════════ */}
        <SafeSection name="THALIA">
        <Suspense fallback={null}>
        <section className="mb-4">
          <THALIACommentary 
            mode="bar" 
            gameState={{
              timeRemaining: displayTimeRemaining,
              potSize: displayPot,
              totalKeys: totalKeysSold,
              recentPurchases: recentPurchaseCount,
              roundNumber: currentRoundId,
              isBloodMoon: currentRoundId % 10 === 0,
              chaosActive: chaosRecentlyTriggered,
              playerStreak: 0,
            }}
          />
        </section>
        </Suspense>
        </SafeSection>

        {/* ═══════════════════════════════════════════════════════════════════
            TICKER TAPE - Live scrolling purchases
            ═══════════════════════════════════════════════════════════════════ */}
        <SafeSection name="TickerTape">
        <Suspense fallback={null}>
        <section className="mb-4 md:mb-6">
          <TickerTape />
        </section>
        </Suspense>
        </SafeSection>

        {/* ═══════════════════════════════════════════════════════════════════
            CHAOS CHIRON - Breaking news ticker for THALIA chaos events
            ═══════════════════════════════════════════════════════════════════ */}
        {activeChaosEvents.length > 0 && (
          <SafeSection name="ChaosChiron">
          <Suspense fallback={null}>
          <section className="mb-4">
            <ChaosChiron events={activeChaosEvents} position="top" />
          </section>
          </Suspense>
          </SafeSection>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            HERO SECTION - TIMER, POT, PRICE
            The most important information, front and center
            Ref tracked for sticky timer visibility
            ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-4 md:mb-8" data-tutorial="timer" ref={heroRef}>
          <HeroStats 
            timeRemaining={displayTimeRemaining} 
            pot={displayPot}
            jackpotPool={displayJackpotPool}
            currentPrice={displayCurrentPrice}
            lastBuyer={displayLastBuyer}
          />
        </section>
        
        {/* ═══════════════════════════════════════════════════════════════════
            ACTIVE CONDITIONS - What's happening right now
            Shows current game state modifiers at a glance
            ═══════════════════════════════════════════════════════════════════ */}
        <SafeSection name="ActiveConditions">
        <Suspense fallback={null}>
        <section className="mb-4">
          <ActiveConditions
            timeLeft={displayTimeRemaining}
            isBloodMoon={currentRoundId % 10 === 0}
            isAssassinMode={assassinActive}
            isFlashCrash={flashCrashActive}
            isFOMOSurge={fomoSurgeActive}
            cascadeLevel={0}
          />
        </section>
        </Suspense>
        </SafeSection>
        
        {/* ═══════════════════════════════════════════════════════════════════
            BUY KEYS & DIVIDENDS - PROMINENTLY FEATURED SIDE BY SIDE
            ═══════════════════════════════════════════════════════════════════ */}
        <section className="mb-4 md:mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 buy-keys-mobile" ref={buyKeysRef}>
          <div data-tutorial="buy-keys">
            <BuyKeys 
              account={account}
              currentPrice={displayCurrentPrice}
              onPurchase={handlePurchase}
              referrerFromUrl={referrerFromUrl}
              fomoSurgeActive={fomoSurgeActive}
              flashCrashActive={flashCrashActive}
              isAssassinMode={assassinActive}
              timeRemaining={displayTimeRemaining}
            />
          </div>
          <div data-tutorial="dividends" className="flex flex-col gap-4">
            <Dividends account={account} onWithdraw={() => setRefreshTrigger(prev => prev + 1)} />
            {/* THALIA Hint - fills space below dividends */}
            <SafeSection name="THALIAHint">
            <Suspense fallback={null}>
              <THALIAHint
                timeLeft={displayTimeRemaining}
                keyPrice={displayCurrentPrice}
                isAssassinMode={assassinActive}
                isFlashCrash={flashCrashActive}
                isFOMOSurge={fomoSurgeActive}
                expanded={true}
              />
            </Suspense>
            </SafeSection>
          </div>
        </section>
        
        {/* ═══════════════════════════════════════════════════════════════════
            LIVE ACTIVITY FEED - Compact ticker below primary actions
            ═══════════════════════════════════════════════════════════════════ */}
        <SafeSection name="LiveActivityFeed">
        <Suspense fallback={null}>
        <section className="mb-4 md:mb-6">
          <LiveActivityFeed />
        </section>
        </Suspense>
        </SafeSection>

        {/* ═══════════════════════════════════════════════════════════════════
            DASHBOARD - Current Pot, Key Price, Last Buyer, Your Dividends
            Prominently displayed above faction powers
            ═══════════════════════════════════════════════════════════════════ */}
        <SafeSection name="Dashboard">
        <Suspense fallback={null}>
        <section className="mb-4 md:mb-8">
          <Dashboard 
            pot={pot}
            currentPrice={currentPrice}
            lastBuyer={lastBuyer}
            account={account}
            refreshTrigger={refreshTrigger}
          />
        </section>
        </Suspense>
        </SafeSection>
        
        {/* ═══════════════════════════════════════════════════════════════════
            SECONDARY CONTENT GRID
            ═══════════════════════════════════════════════════════════════════ */}
            
            {/* Main secondary grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 cards-mobile">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Resistance Points - Player progression & XP */}
                <SafeSection name="ResistancePoints">
                <Suspense fallback={null}>
                  {account && <ResistancePoints account={account} />}
                </Suspense>
                </SafeSection>
                {/* Degen Titles - Achievement Badges */}
                <SafeSection name="DegenTitles">
                <Suspense fallback={null}>
                  {account && <DegenTitles account={account} compact />}
                </Suspense>
                </SafeSection>
                {/* Player Streak - Retention gamification */}
                <SafeSection name="StreakDisplay">
                <Suspense fallback={null}>
                  {account && <StreakDisplay address={account} compact />}
                </Suspense>
                </SafeSection>
                {/* Mini Price Chart */}
                <SafeSection name="PriceChart">
                <Suspense fallback={null}>
                  <PriceChart compact />
                </Suspense>
                </SafeSection>
              </div>
              
              {/* Center Column - Leaderboard + Faction Chat */}
              <div className="space-y-6" data-tutorial="teams">
                <SafeSection name="Leaderboard">
                <Suspense fallback={null}>
                  <Leaderboard refreshTrigger={refreshTrigger} />
                </Suspense>
                </SafeSection>
                {/* Inline Faction Chat - always visible when connected and in a team */}
                {account && playerTeam > 0 && (
                  <TeamChat
                    team={playerTeam}
                    playerAddress={account}
                    isOpen={true}
                    inline
                  />
                )}
              </div>
              
              {/* Right Column - Sharing & Game Log */}
              <div className="space-y-6">
                <SafeSection name="Sharing">
                <Suspense fallback={null}>
                  <Sharing 
                    account={account}
                    pot={pot}
                    timeLeft={timeRemaining}
                  />
                </Suspense>
                </SafeSection>
                {/* Game Log - Persistent event history with explanations */}
                <SafeSection name="GameLog">
                <Suspense fallback={null}>
                  <GameLog />
                </Suspense>
                </SafeSection>
              </div>
            </div>
        
        {/* ═══════════════════════════════════════════════════════════════════
            REFERRAL & ROUND HISTORY SECTION
            ═══════════════════════════════════════════════════════════════════ */}
        <section className="mt-4 md:mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 cards-mobile">
              <SafeSection name="Referral"><Suspense fallback={null}><Referral account={account} /></Suspense></SafeSection>
              <SafeSection name="RoundHistory"><Suspense fallback={null}><RoundHistory refreshTrigger={refreshTrigger} /></Suspense></SafeSection>
              {/* Team Jackpot Claim - show for previous rounds */}
              {currentRoundId > 1 && (
                <SafeSection name="TeamJackpotClaim">
                <Suspense fallback={null}>
                <TeamJackpotClaim 
                  roundId={currentRoundId - 1}
                  onClaimed={() => setRefreshTrigger(prev => prev + 1)}
                />
                </Suspense>
                </SafeSection>
              )}
        </section>
        </>
        )}
        
        {/* ═══════════════════════════════════════════════════════════════════
            FOOTER - 3D GLASSMORPHISM
            ═══════════════════════════════════════════════════════════════════ */}
        <footer className="mt-12 pt-8 border-t border-neon-cyan/10">
          {/* Network Status - Connection health at a glance */}
          <div className="mb-4">
            <SafeSection name="NetworkStatus"><Suspense fallback={null}><NetworkStatusBar /></Suspense></SafeSection>
          </div>
          <div className="glass-panel-3d p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <motion.div
                className="w-2 h-2 rounded-full bg-neon-cyan shadow-[0_0_8px_#00ffff]"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-purple font-orbitron text-lg">
                NO TOMORROW 3D
              </span>
              <motion.div
                className="w-2 h-2 rounded-full bg-neon-magenta shadow-[0_0_8px_#ff0080]"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
              />
            </div>
            <p className="text-gray-600 text-sm font-rajdhani">
              Humanity's last stand against THALIA
            </p>
            <p className="text-gray-700 text-xs mt-2 font-mono tracking-wider">
              ◈ Fighting the Singularity one key at a time ◈
            </p>
            {/* Demo Mode Toggle */}
            {!demoMode.isDemo && (
              <button
                onClick={() => setShowDemoModal(true)}
                className="mt-4 text-xs text-gray-500 hover:text-neon-cyan transition-colors"
              >
                🎮 Try Demo Mode (Practice with fake tokens)
              </button>
            )}
            {/* Legal Links */}
            <div className="mt-4 flex items-center justify-center gap-4">
              <button
                onClick={() => { setCurrentPage('terms'); window.scrollTo(0, 0); }}
                className="text-xs text-gray-600 hover:text-neon-cyan transition-colors font-rajdhani"
              >
                Terms of Service
              </button>
              <span className="text-gray-800">•</span>
              <button
                onClick={() => { setCurrentPage('terms'); window.scrollTo(0, 0); }}
                className="text-xs text-gray-600 hover:text-neon-cyan transition-colors font-rajdhani"
              >
                Privacy Policy
              </button>
            </div>
          </div>
        </footer>
      </Suspense>
      </div>
      
      {/* Floating Help Button - rendered inline in header on md+; show floating on mobile only */}
      <div className="md:hidden">
        <HelpButton />
      </div>
      
      {/* PWA Install Prompt */}
      <InstallPrompt variant="banner" />
      
      {/* Language Switcher - Fixed top right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher variant="compact" />
      </div>
      
      {/* Faction Chat Toggle - removed; chat is now inline below leaderboard */}
      
      {/* Faction Chat Panel - removed; chat is now inline below leaderboard */}
      
      {/* AI Narrative Toast for game events */}
      <SafeSection name="NarrativeToast">
      {narrativeEvent && (
        <NarrativeToast
          event={narrativeEvent}
          roundId={currentRoundId}
          onDismiss={() => setNarrativeEvent(undefined)}
          autoHide={10000}
        />
      )}
      </SafeSection>
      
      {/* WebSocket connection indicator (subtle) */}
      {wsConnected && (
        <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2 px-3 py-1 rounded-full bg-void-black/80 border border-neon-cyan/20 text-xs text-neon-cyan/60">
          <span className="w-2 h-2 rounded-full bg-neon-cyan animate-pulse" />
          Live
        </div>
      )}
    </div>
    </NotificationProvider>
  );
}
